import React, { useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const PortfolioGrid: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const cols = c.columns === '2' || c.columns === 2 ? 2 : 3;

  // Support projects, items, cases as array aliases (Python uses items / projects)
  const projects = (c.projects || c.items || c.cases || [
    { title: 'Brand Refresh', category: 'Branding', image_url: '', description: 'A complete visual overhaul.', url: '#' },
    { title: 'E-Commerce Platform', category: 'Web Dev', image_url: '', description: 'High-converting online store.', url: '#' },
    { title: 'Mobile App', category: 'UI/UX', image_url: '', description: 'Intuitive user experience.', url: '#' },
    { title: 'SEO Campaign', category: 'Marketing', image_url: '', description: 'Tripled organic traffic.', url: '#' },
    { title: 'SaaS Dashboard', category: 'Web App', image_url: '', description: 'Complex data made simple.', url: '#' },
    { title: 'Social Campaign', category: 'Social Media', image_url: '', description: 'Viral engagement strategy.', url: '#' },
  ]) as Array<{ title: string; category: string; image_url: string; description: string; url: string }>;

  // Build unique category list for filter bar (matches Python's "All" + category buttons)
  const categories = ['All', ...Array.from(new Set(projects.map(p => p.category).filter(Boolean)))];
  const showFilters = categories.length > 2; // only show bar when there's meaningful filtering
  const filtered = activeCategory === 'All' ? projects : projects.filter(p => p.category === activeCategory);

  return (
    <section className="sb-render sb-render-portfolio-grid" style={{ padding: '80px 24px', background: 'var(--brand-bg, #fff)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: showFilters ? 32 : 56 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 12, letterSpacing: '-0.025em' }}
          >
            {sfHeadline(c, 'Our Work')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 540, margin: '0 auto' }}
          >
            {sfSub(c, 'A selection of our recent projects.')}
          </InlineEditText>
        </div>

        {/* Category filter bar — mirrors Python renderer's filter buttons */}
        {showFilters && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 40 }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '8px 20px',
                  borderRadius: 9999,
                  border: activeCategory === cat ? 'none' : '1px solid rgba(0,0,0,0.12)',
                  background: activeCategory === cat ? 'var(--brand-primary, #2563eb)' : 'transparent',
                  color: activeCategory === cat ? '#fff' : 'var(--brand-text-light, #64748b)',
                  fontWeight: activeCategory === cat ? 600 : 400,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: 24,
        }}>
          {filtered.map((project, i) => (
            <a
              key={i}
              href={project.url || '#'}
              style={{ textDecoration: 'none', display: 'block', borderRadius: 'var(--brand-card-radius, 12px)', overflow: 'hidden', position: 'relative' }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Image / Placeholder */}
              <div style={{ position: 'relative', paddingTop: '70%', background: '#e2e8f0', overflow: 'hidden' }}>
                {project.image_url ? (
                  <img
                    src={project.image_url}
                    alt={project.title}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                  />
                ) : (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(${135 + i * 20}deg, var(--brand-primary, #2563eb), var(--brand-secondary, #7c3aed))`,
                    opacity: 0.7 + (i % 3) * 0.1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '3rem',
                  }}>
                    🎨
                  </div>
                )}

                {/* Hover Overlay */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(17,24,39,0.85)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  padding: 24,
                  opacity: hoveredIndex === i ? 1 : 0,
                  transition: 'opacity 0.35s ease',
                  textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block',
                    background: 'var(--brand-primary, #2563eb)',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    padding: '4px 12px',
                    borderRadius: 9999,
                    marginBottom: 12,
                  }}>
                    {project.category}
                  </span>
                  <h3 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
                    {project.title}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {project.description}
                  </p>
                  <div style={{
                    marginTop: 16,
                    padding: '8px 20px',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: 'var(--brand-btn-radius, 6px)',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}>
                    View Project →
                  </div>
                </div>
              </div>

              {/* Card footer */}
              <div style={{
                background: 'var(--brand-bg, #fff)',
                padding: '16px 20px',
                borderTop: '1px solid rgba(0,0,0,0.06)',
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--brand-primary, #2563eb)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  {project.category}
                </div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--brand-text, #1e293b)' }}>
                  {project.title}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioGrid;
